import moment from 'moment';
import { fetchAllShifts, Shift } from 'core/API/shifts';
import {
	getDayOfWeekIndex,
	resolveStartOfWeekLabel as resolveSharedStartOfWeekLabel,
} from 'module/schedule/utils/scheduleNavigation';
import { getCurrentUserIdString } from './homePage';

/**
 * Schedule helpers for the employee Home "My Schedule" widget.
 * Shows only published shifts assigned to the current user (not open/house shifts).
 */
type ShiftEmployee = Shift['employee'] | string;

export type ShiftPeriod = 'today' | 'this-week' | 'later';

export function getShiftEmployeeUserId(shift: Shift): string | null {
	const employee = shift.employee as ShiftEmployee | null | undefined;
	if (!employee) return null;

	if (typeof employee === 'object') {
		if (employee.id != null) return employee.id.toString();
		if (typeof employee['@id'] === 'string') {
			return employee['@id'].split('/').pop() || null;
		}
	}

	if (typeof employee === 'string') {
		return (
			employee
				.replace('/api/employees/', '')
				.replace('/api/users/', '')
				.split('/')
				.pop() || null
		);
	}

	return null;
}

export function isShiftAssignedToCurrentUser(shift: Shift, currentUserId: string | null): boolean {
	if (!currentUserId || !shift.employee) return false;
	return getShiftEmployeeUserId(shift) === currentUserId;
}

type DayEntry = { '@id'?: string; id?: number; label?: string };

export function resolveStartOfWeekLabel(
	org?: {
		start_of_week?: string;
		first_day_of_the_week?: string;
	},
	daysData?: DayEntry[] | null,
): string {
	return resolveSharedStartOfWeekLabel(org, daysData);
}

export function getBusinessWeekBounds(startOfWeekLabel = 'Monday'): { start: Date; end: Date } {
	const startDayOfWeek = getDayOfWeekIndex(startOfWeekLabel);
	const currentDate = moment();
	const currentDay = currentDate.day();
	const daysToSubtract = (currentDay - startDayOfWeek + 7) % 7;
	const weekStart = currentDate.clone().subtract(daysToSubtract, 'days').startOf('day');
	const weekEnd = weekStart.clone().add(6, 'days').endOf('day');

	return {
		start: weekStart.toDate(),
		end: weekEnd.toDate(),
	};
}

export function classifyShiftPeriod(
	shiftStartAt: string,
	startOfWeekLabel = 'Monday',
): ShiftPeriod {
	const shiftStart = moment(shiftStartAt);
	const todayStart = moment().startOf('day');

	if (shiftStart.isSame(todayStart, 'day')) {
		return 'today';
	}

	const { start, end } = getBusinessWeekBounds(startOfWeekLabel);
	const weekStartDay = moment(start).startOf('day');
	const weekEndDay = moment(end).startOf('day');

	if (
		shiftStart.isSameOrAfter(weekStartDay, 'day') &&
		shiftStart.isSameOrBefore(weekEndDay, 'day')
	) {
		return 'this-week';
	}

	return 'later';
}

export function getHomePageScheduleDateRange(): { fromDate: string; toDate: string } {
	const start = new Date();
	start.setHours(0, 0, 0, 0);

	const end = new Date(start);
	end.setFullYear(end.getFullYear() + 1);

	const formatDate = (date: Date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	return {
		fromDate: formatDate(start),
		toDate: formatDate(end),
	};
}

export function isUpcomingShift(shift: Shift): boolean {
	const startOfToday = moment().startOf('day');
	return moment(shift.startAt).isSameOrAfter(startOfToday);
}

/**
 * Employee home "My Schedule" shows only published shifts assigned to the current user.
 * Matches SchedulePage visibility for Schedule - My users; open/house shifts are excluded.
 */
export function isShiftVisibleOnEmployeeHomePage(
	shift: Shift,
	currentUserId: string | null,
): boolean {
	if (!currentUserId || !shift.published) return false;
	return isShiftAssignedToCurrentUser(shift, currentUserId);
}

export async function fetchMyPublishedShifts(
	workplaceId: number,
	startOfWeekLabel = 'Monday',
): Promise<Array<Shift & { period: ShiftPeriod }>> {
	const currentUserId = getCurrentUserIdString();
	if (!currentUserId) return [];

	const { fromDate, toDate } = getHomePageScheduleDateRange();
	const response = await fetchAllShifts({ workplaceId, fromDate, toDate });
	const shifts: Shift[] = response?.member ?? [];

	return shifts
		.filter((shift) => isShiftVisibleOnEmployeeHomePage(shift, currentUserId))
		.filter(isUpcomingShift)
		.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
		.map((shift) => ({
			...shift,
			period: classifyShiftPeriod(shift.startAt, startOfWeekLabel),
		}));
}

/** @deprecated Use fetchMyPublishedShifts */
export async function fetchMyUpcomingShifts(workplaceId: number): Promise<Shift[]> {
	return fetchMyPublishedShifts(workplaceId);
}
